import { useCallback, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Upload, ImageIcon, Loader2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { compressImageForUpload } from "@/lib/image-compress";
import { LONG_CACHE_CONTROL } from "@/lib/storage";
import { supabaseImageUrl } from "@/lib/seo";

export interface MediaPickerProps {
  /** Current image URL. Empty string for "no image". */
  value: string;
  /** Called with the chosen URL (a public Supabase Storage URL). */
  onChange: (url: string) => void;
  /** Optional label for the trigger button (default: "Choose image"). */
  buttonLabel?: string;
  /** Prepend a folder to uploads (e.g. "services/", "testimonials/"). */
  folder?: string;
}

const BUCKET = "website-media";

interface StorageObject {
  name: string;
  updated_at: string;
  metadata?: { size?: number; mimetype?: string } | null;
}

/**
 * Reusable image picker for admin forms. Uploads to the `website-media`
 * Supabase Storage bucket, browses existing assets, and hands the public
 * URL back to the caller via `onChange`. Use in place of a raw text URL
 * field anywhere content editors type image URLs today.
 */
export function MediaPicker({ value, onChange, buttonLabel = "Choose image", folder }: MediaPickerProps) {
  const [open, setOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const folderPrefix = folder ? folder.replace(/\/?$/, "/") : "";

  const { data: files, isLoading } = useQuery({
    queryKey: ["website-media", folderPrefix],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .list(folderPrefix || undefined, { limit: 200, sortBy: { column: "updated_at", order: "desc" } });
      if (error) throw error;
      // Map to our narrow shape: Supabase's FileObject is a superset, and a
      // `o is StorageObject` predicate is invalid (StorageObject isn't
      // assignable back to FileObject). Folders have a trailing slash / no name.
      return (data ?? [])
        .filter((o) => !!o.name && !o.name.endsWith("/"))
        .map((o): StorageObject => ({ name: o.name, updated_at: o.updated_at ?? "", metadata: o.metadata }));
    },
  });

  const publicUrl = useCallback(
    (name: string) => supabase.storage.from(BUCKET).getPublicUrl(`${folderPrefix}${name}`).data.publicUrl,
    [folderPrefix]
  );

  const upload = useMutation({
    mutationFn: async (picked: File) => {
      // Resize + re-encode before storing, so a multi-MB photo is stored at web size (#735).
      const file = await compressImageForUpload(picked);
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "-").toLowerCase();
      const key = `${folderPrefix}${Date.now()}-${safeName}`;
      const { error } = await supabase.storage
        .from(BUCKET)
        .upload(key, file, { cacheControl: LONG_CACHE_CONTROL, upsert: false, contentType: file.type || undefined });
      if (error) throw error;
      return supabase.storage.from(BUCKET).getPublicUrl(key).data.publicUrl;
    },
    onSuccess: (url) => {
      qc.invalidateQueries({ queryKey: ["website-media", folderPrefix] });
      onChange(url);
      toast.success("Uploaded");
      setOpen(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) upload.mutate(f);
    e.target.value = "";
  };

  const previewUrl = useMemo(() => value || null, [value]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://…"
          className="flex-1"
        />
        <Button type="button" variant="outline" onClick={() => setOpen(true)}>
          <ImageIcon className="h-4 w-4 mr-1" /> {buttonLabel}
        </Button>
      </div>
      {previewUrl && (
        <img
          src={previewUrl}
          alt="Preview"
          className="max-h-32 rounded border border-border object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Media library{folderPrefix ? ` · /${folderPrefix.replace(/\/$/, "")}` : ""}</DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2">
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button type="button" onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
              {upload.isPending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Upload new
            </Button>
            <span className="text-xs text-muted-foreground">
              Images upload to Supabase Storage. Paste an external URL in the text field instead for stock photos.
            </span>
          </div>

          {isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
          ) : !files || files.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              No files uploaded yet. Click &quot;Upload new&quot; above to add one.
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 py-2">
              {files.map((f) => {
                const url = publicUrl(f.name);
                // The tile shows a small WebP, but the form still gets the original's URL (#1287).
                const thumb = supabaseImageUrl(url, { width: 320, resize: "contain" }) ?? url;
                const selected = value === url;
                return (
                  <button
                    key={f.name}
                    type="button"
                    onClick={() => { onChange(url); setOpen(false); }}
                    className={`group relative rounded-md overflow-hidden border transition-colors ${selected ? "border-primary ring-2 ring-primary" : "border-border hover:border-primary/60"}`}
                    aria-label={`Select ${f.name}`}
                  >
                    <img src={thumb} alt={f.name} className="w-full aspect-square object-cover" loading="lazy" />
                    <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-0.5 truncate">
                      {f.name}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => { onChange(""); setOpen(false); }}>
              <LinkIcon className="h-4 w-4 mr-1" /> Clear
            </Button>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
